import {
  EAiActionStatus,
  EDatasourceType,
  EVisionNodeTypes,
  EVisionTemplateTypes,
  getDefaultNodeTitle,
  IVisionEdge,
  IVisionNode,
  IVisionWorkflowData
} from "@kuika/kuika-cl-model";
import { notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactFlow, {
  addEdge,
  Background,
  BackgroundVariant,
  Connection,
  Edge,
  MiniMap,
  Node,
  NodeChange,
  useEdgesState,
  useNodesState,
  useReactFlow
} from "reactflow";
import "reactflow/dist/style.css";
import KuikaButton, { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorIdentity, KuikaEditorTitle } from "../../../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaPageHeader from "../../../../ui-kit/kuika-page-header/kuika-page-header";
import KuikaSplitButton from "../../../../ui-kit/kuika-split-button/kuika-split-button";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { toast } from "../../../../ui-kit/kuika-toast";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import SharedReactFlowControls from "../../common/react-flow-controls/react-flow-controls";
import { getEditorGlyph, IEditorGlyph } from "../../editor-switcher/editor-glyph";
import { EditorGlyphIcon } from "../../editor-switcher/editor-glyph-icon";
import { useDnD } from "./dnd-context";
import FloatingNodePalette from "./FloatingNodePalette";
import { HistoryProvider } from "./history-context";
import AnalyticsProcessorNode from "./nodes/AnalyticsProcessorNode";
import ApiGatewayNode from "./nodes/ApiGatewayNode";
import DashboardOutputNode from "./nodes/DashboardOutputNode";
import DatabaseServiceNode from "./nodes/DatabaseServiceNode";
import MetricsCalculatorNode from "./nodes/MetricsCalculatorNode";
import ProcessorNode from "./nodes/ProcessorNode";
import ReportGeneratorNode from "./nodes/ReportGeneratorNode";
import StartNode from "./nodes/StartNode";
import { setHistoryCallback } from "./nodes/useNodeCreation";
import VideoInputNode from "./nodes/VideoInputNode";
import { useUndoRedo } from "./useUndoRedo";
import { isValidConnection as checkConnectionValidity } from "./VisionConnectionRules";
import { useWorkflow } from "./workflow-context";
import styles from "./WorkflowBuilder.module.css";

// Register custom node types - using camelCase to match backend EnumMember values
const nodeTypes = {
  startNode: StartNode,
  videoInput: VideoInputNode,
  detector: ProcessorNode,
  tracker: ProcessorNode,
  segmentation: ProcessorNode,
  analyticsProcessor: AnalyticsProcessorNode,
  metricsCalculator: MetricsCalculatorNode,
  dashboardOutput: DashboardOutputNode,
  reportGenerator: ReportGeneratorNode,
  databaseService: DatabaseServiceNode,
  apiGateway: ApiGatewayNode
};

// The start node ID - this node is always present and cannot be deleted
const START_NODE_ID = "start-node";

/** The bar's glyph when there is no switcher to draw it: the AI family's vision glyph. */
const VISION_ACTION_GLYPH: IEditorGlyph = getEditorGlyph({ kind: "ai", actionType: EDatasourceType.VisionAction });

// Layout constants for auto-arrange
const LAYOUT = {
  NODE_WIDTH: 220,
  NODE_HEIGHT: 120,
  HORIZONTAL_GAP: 100,
  VERTICAL_GAP: 60,
  START_X: 50,
  START_Y: 200
};

// Default start node that's always present in workflows
const createStartNode = (position?: { x: number; y: number }): Node => ({
  id: START_NODE_ID,
  type: "startNode",
  position: position || { x: LAYOUT.START_X, y: LAYOUT.START_Y },
  data: {
    label: "Start",
    title: "Start",
    type: "startNode"
  },
  deletable: false,
  draggable: true,
  selectable: true
});

/**
 * Auto-arrange nodes using a simple left-to-right topological layout.
 * Places nodes in columns based on their depth in the workflow graph.
 */
const autoArrangeNodes = (nodes: Node[], edges: Edge[]): Node[] => {
  if (nodes.length === 0) return nodes;

  // Build adjacency list and calculate node depths (columns)
  const adjacencyList = new Map<string, string[]>();
  const incomingEdges = new Map<string, string[]>();

  nodes.forEach((node) => {
    adjacencyList.set(node.id, []);
    incomingEdges.set(node.id, []);
  });

  edges.forEach((edge) => {
    const targets = adjacencyList.get(edge.source) || [];
    targets.push(edge.target);
    adjacencyList.set(edge.source, targets);

    const sources = incomingEdges.get(edge.target) || [];
    sources.push(edge.source);
    incomingEdges.set(edge.target, sources);
  });

  // Calculate depth for each node using BFS from start node
  const depths = new Map<string, number>();
  const queue: { id: string; depth: number }[] = [];

  // Start with the start node at depth 0
  const startNode = nodes.find((n) => n.id === START_NODE_ID);
  if (startNode) {
    queue.push({ id: START_NODE_ID, depth: 0 });
    depths.set(START_NODE_ID, 0);
  }

  // Also find nodes with no incoming edges (source nodes) that aren't start node
  nodes.forEach((node) => {
    if (node.id !== START_NODE_ID) {
      const incoming = incomingEdges.get(node.id) || [];
      if (incoming.length === 0 || (incoming.length === 1 && incoming[0] === START_NODE_ID)) {
        if (!depths.has(node.id)) {
          queue.push({ id: node.id, depth: 1 });
          depths.set(node.id, 1);
        }
      }
    }
  });

  // BFS to assign depths
  while (queue.length > 0) {
    const { id, depth } = queue.shift()!;
    const targets = adjacencyList.get(id) || [];

    targets.forEach((targetId) => {
      const currentDepth = depths.get(targetId);
      const newDepth = depth + 1;
      if (currentDepth === undefined || newDepth > currentDepth) {
        depths.set(targetId, newDepth);
        queue.push({ id: targetId, depth: newDepth });
      }
    });
  }

  // Assign depth 1 to any remaining unvisited nodes
  nodes.forEach((node) => {
    if (!depths.has(node.id)) {
      depths.set(node.id, 1);
    }
  });

  // Group nodes by depth (column)
  const columns = new Map<number, Node[]>();
  nodes.forEach((node) => {
    const depth = depths.get(node.id) || 0;
    const column = columns.get(depth) || [];
    column.push(node);
    columns.set(depth, column);
  });

  // Position nodes
  const arrangedNodes: Node[] = [];
  columns.forEach((columnNodes, depth) => {
    const x = LAYOUT.START_X + depth * (LAYOUT.NODE_WIDTH + LAYOUT.HORIZONTAL_GAP);
    const totalHeight = columnNodes.length * LAYOUT.NODE_HEIGHT + (columnNodes.length - 1) * LAYOUT.VERTICAL_GAP;
    const startY = LAYOUT.START_Y - totalHeight / 2 + LAYOUT.NODE_HEIGHT / 2;

    columnNodes.forEach((node, index) => {
      const y = startY + index * (LAYOUT.NODE_HEIGHT + LAYOUT.VERTICAL_GAP);
      arrangedNodes.push({
        ...node,
        position: { x, y }
      });
    });
  });

  return arrangedNodes;
};

interface WorkflowBuilderProps {
  onSave?: (nodes: Node[], edges: Edge[], workflowName: string, status?: EAiActionStatus) => void;
  onCancel?: () => void;
  onChange?: (nodes: Node[], edges: Edge[], workflowName: string) => void;
  initialNodes?: Node[];
  initialEdges?: Edge[];
  initialName?: string;
  isEditing?: boolean;
  /** The identity chip, from the host — the vision actions this editor can open. */
  switcher?: React.ReactNode;
  /**
   * Where the host's save stands. The host runs the save and decides when the editor leaves, so
   * it also owns the order the commit shows: "Saving", then "Saved" held before the close.
   * Without it the commit only shows that it is busy.
   */
  saveStatus?: KuikaButtonStatus;
}

export type { WorkflowBuilderProps };

let id = 0;
const getId = () => `node_${id++}`;

const WorkflowBuilder = ({
  onSave,
  onCancel,
  onChange,
  initialNodes = [],
  initialEdges = [],
  initialName,
  isEditing = false,
  switcher,
  saveStatus
}: WorkflowBuilderProps) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  /**
   * Ensure start node is always present in initial nodes.
   * Positions the start node to the left of existing nodes to avoid overlap.
   */
  const getNodesWithStart = (nodes: Node[]): Node[] => {
    const hasStartNode = nodes.some((n) => n.id === START_NODE_ID);
    if (hasStartNode) {
      return nodes;
    }

    // Find the leftmost and center Y position of existing nodes
    if (nodes.length > 0) {
      const minX = Math.min(...nodes.map((n) => n.position.x));
      const avgY = nodes.reduce((sum, n) => sum + n.position.y, 0) / nodes.length;
      // Position start node to the left of all existing nodes
      const startPosition = {
        x: minX - LAYOUT.NODE_WIDTH - LAYOUT.HORIZONTAL_GAP,
        y: avgY
      };
      return [createStartNode(startPosition), ...nodes];
    }

    return [createStartNode(), ...nodes];
  };

  /**
   * Auto-connect start node to the first node in the workflow (typically VideoInput).
   * This is needed when loading templates that don't include the start node.
   */
  const getEdgesWithStartConnection = (nodes: Node[], edges: Edge[]): Edge[] => {
    // Check if there's already an edge from start node
    const hasStartEdge = edges.some((e) => e.source === START_NODE_ID);
    if (hasStartEdge) {
      return edges;
    }

    // Find the first VideoInput node or the first node that has no incoming edges
    const videoInputNode = nodes.find((n) => n.type === "videoInput");

    if (videoInputNode) {
      // Check if this node already has an incoming edge from start node
      const alreadyConnected = edges.some((e) => e.target === videoInputNode.id && e.source === START_NODE_ID);
      if (!alreadyConnected) {
        // Create an edge from start node to the video input
        const startEdge: Edge = {
          id: `edge-start-to-${videoInputNode.id}`,
          source: START_NODE_ID,
          target: videoInputNode.id
        };
        return [startEdge, ...edges];
      }
    }

    return edges;
  };

  const [nodes, setNodes, onNodesChange] = useNodesState(getNodesWithStart(initialNodes));
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    getEdgesWithStartConnection(getNodesWithStart(initialNodes), initialEdges)
  );
  const { project, fitView } = useReactFlow();
  const { type, setType } = useDnD();
  const { workflowName, setWorkflowName, isDirty, setIsDirty } = useWorkflow();

  // Undo/Redo functionality
  const { canUndo, canRedo, undo, redo, takeSnapshot, clearHistory, pushToFuture, pushToPast } =
    useUndoRedo() as ReturnType<typeof useUndoRedo> & {
      pushToFuture: (nodes: Node[], edges: Edge[]) => void;
      pushToPast: (nodes: Node[], edges: Edge[]) => void;
    };

  // Register the history callback for useNodeCreation hook
  useEffect(() => {
    setHistoryCallback(takeSnapshot);
    return () => setHistoryCallback(null);
  }, [takeSnapshot]);

  /**
   * Custom onNodesChange handler that prevents deletion of the start node.
   * Filters out any 'remove' changes targeting the start node.
   * Also takes a snapshot before node removal for undo support.
   */
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Filter out any remove changes for the start node
      const filteredChanges = changes.filter((change) => {
        if (change.type === "remove" && change.id === START_NODE_ID) {
          return false;
        }
        return true;
      });

      // Take snapshot before removing nodes (for undo)
      const hasRemoval = filteredChanges.some((change) => change.type === "remove");
      if (hasRemoval) {
        takeSnapshot(nodes, edges, workflowName);
      }

      onNodesChange(filteredChanges);
    },
    [onNodesChange, nodes, edges, workflowName, takeSnapshot]
  );

  /**
   * Custom onEdgesChange handler that takes a snapshot before edge removal for undo support.
   */
  const handleEdgesChange = useCallback(
    (changes: import("reactflow").EdgeChange[]) => {
      // Take snapshot before removing edges (for undo)
      const hasRemoval = changes.some((change) => change.type === "remove");
      if (hasRemoval) {
        takeSnapshot(nodes, edges, workflowName);
      }
      onEdgesChange(changes);
    },
    [onEdgesChange, nodes, edges, workflowName, takeSnapshot]
  );

  // Workflow name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(initialName || workflowName);

  // Saving state for loading spinner
  const [isSaving, setIsSaving] = useState(false);
  const [isMiniMapVisible, setIsMiniMapVisible] = useState(true);

  // Set initial name when component mounts
  // Using a ref to ensure we only set once on mount
  const hasSetInitialName = useRef(false);
  if (!hasSetInitialName.current && initialName) {
    setWorkflowName(initialName);
    hasSetInitialName.current = true;
  }

  // Notify parent of changes (excluding the start node which is visual-only)
  useEffect(() => {
    if (onChange) {
      const nodesWithoutStart = nodes.filter((n) => n.id !== START_NODE_ID);
      const edgesWithoutStart = edges.filter((e) => e.source !== START_NODE_ID && e.target !== START_NODE_ID);
      onChange(nodesWithoutStart, edgesWithoutStart, workflowName);
    }
  }, [nodes, edges, workflowName, onChange]);

  /**
   * Validate and handle new connections between nodes.
   * Uses the VisionConnectionRules to check if the connection is valid
   * based on data flow types (e.g., VideoStream can only go to Detector/Segmentation).
   */
  const onConnect = useCallback(
    (params: Connection) => {
      // Get source and target node types
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);

      if (!sourceNode || !targetNode) {
        toast.error("Cannot connect: node not found");
        return;
      }

      const sourceType = sourceNode.type || "";
      const targetType = targetNode.type || "";

      // Validate the connection using our rules
      const validationResult = checkConnectionValidity(sourceType, targetType);

      if (!validationResult.valid) {
        // Show user-friendly error message
        toast.error(validationResult.shortMessage);
        return;
      }

      // Take snapshot before adding connection (for undo)
      takeSnapshot(nodes, edges, workflowName);

      // Connection is valid - add it
      setEdges((eds) => addEdge(params, eds));
      setIsDirty(true);
    },
    [nodes, edges, workflowName, setEdges, setIsDirty, takeSnapshot]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!type || !reactFlowWrapper.current) {
        return;
      }

      // Take snapshot before adding node (for undo)
      takeSnapshot(nodes, edges, workflowName);

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top
      });

      const newNode: Node = {
        id: getId(),
        type,
        position,
        data: {
          label: `${type} node`,
          title: getDefaultNodeTitle(type),
          type // Add type to data for ProcessorNode
        }
      };

      setNodes((nds) => nds.concat(newNode));

      // Auto-connect start node to VideoInput when it's the first one dropped
      if (type === "videoInput") {
        setEdges((eds) => {
          // Check if start node already has an outgoing connection
          const startHasConnection = eds.some((e) => e.source === START_NODE_ID);
          if (!startHasConnection) {
            const startEdge: Edge = {
              id: `edge-start-to-${newNode.id}`,
              source: START_NODE_ID,
              target: newNode.id
            };
            return [startEdge, ...eds];
          }
          return eds;
        });
      }

      setType(null);
      setIsDirty(true);
    },
    [project, type, nodes, edges, workflowName, setNodes, setEdges, setType, setIsDirty, takeSnapshot]
  );

  const handleSave = async (status: EAiActionStatus = EAiActionStatus.Published) => {
    if (onSave) {
      setIsSaving(true);
      try {
        // Filter out the start node - it's only a visual indicator
        const nodesToSave = nodes.filter((n) => n.id !== START_NODE_ID);
        // Filter out edges connected to start node
        const edgesToSave = edges.filter((e) => e.source !== START_NODE_ID && e.target !== START_NODE_ID);
        await onSave(nodesToSave, edgesToSave, workflowName, status);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleExport = () => {
    // Generate a unique ID for the workflow
    const workflowId = `workflow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Filter out the start node - it's only a visual indicator, not part of actual workflow
    const workflowNodes = nodes.filter((n) => n.id !== START_NODE_ID);
    // Filter out edges connected to start node
    const workflowEdges = edges.filter((e) => e.source !== START_NODE_ID && e.target !== START_NODE_ID);

    // Transform ReactFlow nodes to VisionNodeModel format for backend
    // Node types are already in PascalCase (matching backend), so no conversion needed
    // Keep nested data.config structure to match dbJSON2.json format
    const visionNodes: IVisionNode[] = workflowNodes.map((node) => {
      const nodeType = node.type || "";
      const nodeConfig = { ...node.data?.config };

      // Clean up data based on node type
      if (nodeType === EVisionNodeTypes.Detector) {
        // Convert confidence from decimal (0-1) to integer (0-100) if needed
        if (nodeConfig.confidence !== undefined && typeof nodeConfig.confidence === "number") {
          nodeConfig.confidence =
            nodeConfig.confidence <= 1 ? Math.round(nodeConfig.confidence * 100) : Math.round(nodeConfig.confidence);
        }
      }

      return {
        id: node.id,
        type: nodeType,
        displayName: node.data?.title || node.data?.label || getDefaultNodeTitle(nodeType),
        position: {
          x: node.position.x,
          y: node.position.y
        },
        // Keep width/height on nodes (from ReactFlow)
        width: (node as any).width,
        height: (node as any).height,
        // Keep nested data structure with config
        data: {
          title: node.data?.title || getDefaultNodeTitle(nodeType),
          type: nodeType,
          config: nodeConfig
        }
      };
    });

    // Transform ReactFlow edges to VisionEdgeModel format
    const visionEdges: IVisionEdge[] = workflowEdges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      ...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
      ...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {})
    }));

    // Create VisionInfoModel structure matching backend
    const workflowData: IVisionWorkflowData = {
      id: workflowId,
      title: workflowName || "Untitled Workflow",
      description: `Exported workflow with ${workflowNodes.length} nodes and ${workflowEdges.length} connections`,
      type: EVisionTemplateTypes.Custom,
      nodes: visionNodes,
      edges: visionEdges
    };

    // Convert to JSON string with pretty formatting
    const jsonString = JSON.stringify(workflowData, null, 2);

    // Create a Blob from the JSON string
    const blob = new Blob([jsonString], { type: "application/json" });

    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T")[0];
    const filename = `${workflowName || "workflow"}_${timestamp}.json`;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCancelClick = () => {
    onCancel?.();
  };

  const handleClearCanvas = () => {
    // Take snapshot before clearing (for undo)
    takeSnapshot(nodes, edges, workflowName);
    // Keep only the start node when clearing
    setNodes([createStartNode()]);
    setEdges([]);
    setIsDirty(true);
  };

  /**
   * Auto-arrange all nodes in a clean left-to-right layout based on their connections.
   */
  const handleAutoArrange = () => {
    // Take snapshot before arranging (for undo)
    takeSnapshot(nodes, edges, workflowName);
    const arrangedNodes = autoArrangeNodes(nodes, edges);
    setNodes(arrangedNodes);
    setIsDirty(true);
    // Fit view to show all arranged nodes
    setTimeout(() => fitView(), 0);
  };

  /**
   * Undo the last action - restores previous state of nodes, edges, and workflow name.
   */
  const handleUndo = useCallback(() => {
    const previousState = undo();
    if (previousState) {
      // Save current state to future stack before restoring
      pushToFuture(nodes, edges, workflowName);
      // Restore the previous state
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      if (previousState.workflowName) {
        setWorkflowName(previousState.workflowName);
      }
      setIsDirty(true);
    }
  }, [undo, pushToFuture, nodes, edges, workflowName, setNodes, setEdges, setWorkflowName, setIsDirty]);

  /**
   * Redo the last undone action - restores the state that was undone including workflow name.
   */
  const handleRedo = useCallback(() => {
    const nextState = redo();
    if (nextState) {
      // Save current state to past stack before restoring
      pushToPast(nodes, edges, workflowName);
      // Restore the next state
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      if (nextState.workflowName) {
        setWorkflowName(nextState.workflowName);
      }
      setIsDirty(true);
    }
  }, [redo, pushToPast, nodes, edges, workflowName, setNodes, setEdges, setWorkflowName, setIsDirty]);

  // Keyboard shortcuts for undo/redo and delete
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if user is typing in an input field
      const activeElement = document.activeElement;
      const isTyping =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.getAttribute("contenteditable") === "true");

      // Check for Cmd+Z (Mac) or Ctrl+Z (Windows/Linux) for undo
      if ((event.metaKey || event.ctrlKey) && event.key === "z" && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
      }
      // Check for Cmd+Shift+Z (Mac) or Ctrl+Shift+Z / Ctrl+Y (Windows/Linux) for redo
      if ((event.metaKey || event.ctrlKey) && (event.key === "y" || (event.key === "z" && event.shiftKey))) {
        event.preventDefault();
        handleRedo();
      }
      // Check for Delete or Backspace key to delete selected nodes
      // Don't delete nodes if user is typing in an input field
      if ((event.key === "Delete" || event.key === "Backspace") && !isTyping) {
        // Find selected nodes that are deletable (not the start node)
        const selectedNodes = nodes.filter((node) => node.selected && node.id !== START_NODE_ID);
        if (selectedNodes.length > 0) {
          event.preventDefault();
          // Take snapshot before deleting
          takeSnapshot(nodes, edges, workflowName);
          // Remove selected nodes
          setNodes((nds) => nds.filter((node) => !node.selected || node.id === START_NODE_ID));
          setIsDirty(true);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo, handleRedo, nodes, edges, takeSnapshot, setNodes, setIsDirty]);

  const handleEditNameClick = () => {
    setEditedName(workflowName);
    setIsEditingName(true);
  };

  const handleConfirmNameEdit = () => {
    if (editedName.trim()) {
      // Take a snapshot before changing the name to track it in undo/redo history
      takeSnapshot(nodes, edges, workflowName);
      setWorkflowName(editedName.trim());
      setIsEditingName(false);
      setIsDirty(true);
    }
  };

  const handleCancelNameEdit = () => {
    setEditedName(workflowName);
    setIsEditingName(false);
  };

  const handleNameInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleConfirmNameEdit();
    } else if (e.key === "Escape") {
      handleCancelNameEdit();
    }
  };

  /**
   * ReactFlow's isValidConnection callback - called during edge dragging
   * to determine if a connection can be made. Returns false to prevent
   * the edge from snapping to invalid targets.
   *
   * Note: This is called frequently during drag, so we show the error message
   * only when the user releases the mouse (via onConnectEnd) to avoid spam.
   */
  const lastInvalidConnection = useRef<{
    source: string;
    target: string;
    shortMessage: string;
    detail: string;
  } | null>(null);

  const isValidConnection = useCallback(
    (connection: Connection) => {
      const sourceNode = nodes.find((n) => n.id === connection.source);
      const targetNode = nodes.find((n) => n.id === connection.target);

      if (!sourceNode || !targetNode) {
        return false;
      }

      const result = checkConnectionValidity(sourceNode.type || "", targetNode.type || "");

      if (!result.valid) {
        // Store the invalid connection info to show error when user releases
        lastInvalidConnection.current = {
          source: connection.source || "",
          target: connection.target || "",
          shortMessage: result.shortMessage,
          detail: result.detail
        };
      } else {
        lastInvalidConnection.current = null;
      }

      return result.valid;
    },
    [nodes]
  );

  /**
   * Called when user releases the edge being dragged.
   * If they were trying to make an invalid connection, show the error.
   */
  const onConnectEnd = useCallback((event: MouseEvent | TouchEvent) => {
    if (lastInvalidConnection.current) {
      const { shortMessage, detail } = lastInvalidConnection.current;

      // Show notification with expandable detail
      const key = `connection-error-${Date.now()}`;

      const showDetail = () => {
        notification.error({
          message: shortMessage,
          description: detail,
          duration: 0, // Stay until manually closed
          key,
          className: styles.connectionNotice
        });
      };

      // antd notifications portal to <body> and paint a white card, so the notice carries its own
      // class: the kit toast can't host the "Why?" link or a notice that stays until closed.
      notification.error({
        message: (
          <div className={styles.connectionNoticeMessage}>
            <span>{shortMessage}</span>
            <span onClick={showDetail} className={styles.connectionNoticeWhy}>
              Why?
            </span>
          </div>
        ),
        duration: 4,
        key,
        className: styles.connectionNotice
      });

      lastInvalidConnection.current = null;
    }
  }, []);

  return (
    <div className="kuika__data-diagram__create-con kuika__data-diagram__create-con--bar vision">
      <div className={styles.workflowBuilder}>
        <KuikaPageHeader
          variant="bar"
          title={workflowName}
          onBack={handleCancelClick}
          backLabel="Back to AI Functions"
          renderTitle={(): React.ReactNode => (
            <KuikaEditorTitle
              entityLabel="vision action"
              isRenaming={isEditingName}
              canAbortRename={!!workflowName}
              onStartRename={handleEditNameClick}
              onConfirmRename={handleConfirmNameEdit}
              onAbortRename={handleCancelNameEdit}
              editTestId="visionActionNameEditButton"
              confirmTestId="visionActionNameConfirmButton"
              abortTestId="visionActionNameCancelButton"
              identity={
                switcher ?? (
                  <KuikaEditorIdentity
                    icon={<EditorGlyphIcon glyph={VISION_ACTION_GLYPH} />}
                    iconTooltip={VISION_ACTION_GLYPH.label}
                    name={workflowName}
                  />
                )
              }
              renameField={
                <KuikaInput
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleNameInputKeyDown}
                  autoFocus
                  style={{ width: "300px" }}
                  placeholder="Enter workflow name"
                />
              }
            />
          )}
          actions={
            <span className={styles.headerRight}>
              <KuikaTooltip content={canUndo ? "Undo (Ctrl+Z)" : "Nothing to undo"}>
                <span>
                  <KuikaButton
                    onClick={handleUndo}
                    type="tertiary"
                    disabled={!canUndo}
                    style={{ minWidth: "auto", padding: "8px 12px" }}
                  >
                    <KuikaSvg
                      type="arrows"
                      name="arrow-back-up"
                      width={16}
                      height={16}
                      color={canUndo ? "var(--blue-5)" : "var(--dark-5)"}
                    />
                  </KuikaButton>
                </span>
              </KuikaTooltip>
              <KuikaTooltip content={canRedo ? "Redo (Ctrl+Shift+Z)" : "Nothing to redo"}>
                <span>
                  <KuikaButton
                    onClick={handleRedo}
                    type="tertiary"
                    disabled={!canRedo}
                    style={{ minWidth: "auto", padding: "8px 12px" }}
                  >
                    <KuikaSvg
                      type="arrows"
                      name="arrow-forward-up"
                      width={16}
                      height={16}
                      color={canRedo ? "var(--blue-5)" : "var(--dark-5)"}
                    />
                  </KuikaButton>
                </span>
              </KuikaTooltip>
              <KuikaButton onClick={handleClearCanvas} type="tertiary" color="danger" uppercase={true}>
                Clear Canvas
              </KuikaButton>
              <KuikaButton type="secondary" onClick={handleExport} uppercase={true} disabled={isEditingName}>
                Export
              </KuikaButton>
              <KuikaSplitButton
                onClick={() => handleSave(EAiActionStatus.Published)}
                status={saveStatus ?? (isSaving ? "loading" : "idle")}
                disabled={isEditingName}
                menuLabel="More save actions"
                aria-label="Save actions"
                options={[
                  {
                    key: "save-draft",
                    label: "Save as Draft",
                    onClick: () => handleSave(EAiActionStatus.Draft)
                  }
                ]}
              >
                {/* Publishes, but labelled for what the user is doing: UPDATE on an action
                  that already exists, SAVE while creating one. `isEditing` was already
                  passed in and never read. */}
                {isEditing ? "UPDATE" : "SAVE"}
              </KuikaSplitButton>
            </span>
          }
        />

        <div className={styles.canvasContainer} ref={reactFlowWrapper}>
          <HistoryProvider takeSnapshot={takeSnapshot}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={onConnect}
              onConnectEnd={onConnectEnd}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              isValidConnection={isValidConnection}
              fitView
              attributionPosition="bottom-left"
            >
              <SharedReactFlowControls
                showAutoArrange
                onAutoArrange={handleAutoArrange}
                showMiniMapToggle
                isMiniMapVisible={isMiniMapVisible}
                onToggleMiniMap={() => setIsMiniMapVisible((prev) => !prev)}
              />
              {isMiniMapVisible && <MiniMap nodeStrokeWidth={3} zoomable pannable />}
              <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
              <FloatingNodePalette />
            </ReactFlow>
          </HistoryProvider>

          {/* <Sidebar /> */}
        </div>
      </div>
    </div>
  );
};

export default WorkflowBuilder;
